import React, {useEffect} from 'react';
import styles from './_curated-vehicle-details-page.module.scss';
import {
  hasValue, LoadingPanel, VehicleDetailsTabs,
} from "@amsanalytics/react-shared-library";
import {useSelector} from "react-redux";
import {AppState} from "../../../Store/rootReducer";
import {useAppDispatch} from "../../../Store/useAppDispatch";
import {
  clearCuratedVehicleDetailsSuccess, 
  getDownPaymentHistories,
  loadCuratedApplicationDetails,
  loadCuratedVehicleDetail
} from "../../../Store/CuratedVehicles/actions";
import {useNavigate, useParams} from "react-router";
import {loadLibrary, setClient} from "../../../Store/Shared/actions";
import MessageViewer from "../../Controls/MessageViewer/MessageViewer";
import {encodeDepositPageParameters} from "../CuratedVehiclesPage/CuratedVehiclesUtils";
import {MS_PER_MINUTE} from "@progress/kendo-date-math";
import {injectScript} from "../../../Helpers/ScriptInjectionHelpers";
import useThirdPartyChatbot from "../../../Hooks/useThirdPartyChatbot";

export interface CuratedVehiclesPageProps {
  headerAndFooterHeight: number;
}

export default function CuratedVehicleDetailsPage (props:  CuratedVehiclesPageProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const clientId = (params as any)?.clientId;
  const vehicleId = (params as any)?.vehicleId;
  const customerPortalLinkId = (params as any)?.customerPortalLinkId;
  
  const loadingDetail = useSelector((state: AppState) => state.curatedVehicles.loading);
  const selectedVehicleLoaded = loadingDetail.selectedVehicleLoaded;
  const applicationDetailsLoaded = loadingDetail.applicationDetailsLoaded;
  const isLoading = useSelector((state: AppState) => !applicationDetailsLoaded || !selectedVehicleLoaded);
  const applicationDetails = useSelector((state: AppState) => state.curatedVehicles.application);
  const selectedVehicle = useSelector((state: AppState) => state.curatedVehicles.selectedVehicle);
  const selectedFilters = applicationDetails?.selectedFilters ?? null; // This is set and used in case the user navigates away and comes back.
  const downPaymentHistories = useSelector((state: AppState) => state.curatedVehicles.downPaymentHistories);

  useThirdPartyChatbot(applicationDetails?.chatbotScriptUrl);


  const onMakeDownPayment = (vehicleId: string): void => {
    if (!selectedVehicle || !applicationDetails || !selectedFilters) {
      return;
    }
    
    const encodedParams = encodeDepositPageParameters(
      customerPortalLinkId, 
      selectedVehicle.stockNumber, 
      applicationDetails.applicationId, 
      applicationDetails.fixedDepositAmount ?? selectedFilters.downPayment
    );
    navigate(`/${clientId}/deposit/${encodedParams}`);
  };

  const handleReturnToInventory = (): void => {
    dispatch(clearCuratedVehicleDetailsSuccess());
    navigate(`/${clientId}/curated/${customerPortalLinkId}`);
  };

  useEffect(() => {
    // Load lookups if we have enough info.
    const clientIdFromParams = (params as any)?.clientId;
    if (clientIdFromParams) {
      if (clientId !== clientIdFromParams) {
        dispatch(setClient((params as any)?.clientId));
      }

      if (!applicationDetailsLoaded) {
        dispatch(loadCuratedApplicationDetails(clientIdFromParams, customerPortalLinkId));
      }
      
      if (hasValue(vehicleId)) {
        dispatch(loadCuratedVehicleDetail(clientIdFromParams, vehicleId, null));
      }
      dispatch(loadLibrary(clientId));
    }
  }, []);

  useEffect(() => {
    if (hasValue(applicationDetails?.applicationId) && hasValue(clientId)) {
      // Once when it loads
      dispatch(getDownPaymentHistories(applicationDetails!.applicationId, clientId));

      // Repeats every X minutes
      let timer = setInterval(async () => {
        dispatch(getDownPaymentHistories(applicationDetails!.applicationId, clientId));
      }, 2 * MS_PER_MINUTE); // 2 Minutes
      return () => clearInterval(timer);
    }
  }, [applicationDetails?.applicationId, clientId]);

  // Redirect to the thank you page if they've already made a payment
  if (downPaymentHistories.length > 0 && customerPortalLinkId) {
    navigate(`/${clientId}/payment-thank-you/${customerPortalLinkId}`);
  }

  // The Impel 360 viewer is enabled per client by setting impel360ScriptUrl in CuratedSiteConfig;
  // a null/empty/whitespace value means the viewer is off.
  const impelScriptSource = applicationDetails?.impel360ScriptUrl?.trim() || null;

  useEffect(() => {
    if (!impelScriptSource) {
      return;
    }

    injectScript(impelScriptSource);
  }, [impelScriptSource]);


  return (
    <div className={styles.webViewVehicleDetailsPageContainer}>
      {isLoading &&
        <div style={{position: 'absolute', zIndex: 1000, top: `calc(0% + ${props.headerAndFooterHeight}px)`, width: '100%', height: '100%'}}>
          <LoadingPanel label={'Loading vehicle details...'}/>
        </div>
      }
      {applicationDetails && selectedVehicle &&
        <VehicleDetailsTabs
          selectedVehicle={selectedVehicle}
          showVehicleLength={applicationDetails.showVehicleLength}
          slidesOrDoors={applicationDetails.slidesOrDoors}
          onBackToInventory={handleReturnToInventory}
          onMakeDownPayment={onMakeDownPayment}
          headerAndFooterHeight={props.headerAndFooterHeight + 20} // 20 accounts for the padding
          makeDepositButtonText={applicationDetails.makeDepositButtonText}
          useImpel360Viewer={hasValue(impelScriptSource)}
        />
      }
      <MessageViewer />
    </div>
  );
}
